import api from './api';

/** Largo de `SvcBienes.CODIGOBIEN` (char(30), no Unicode). */
export const MAX_UNIDAD_CODIGO = 30;

export interface VehiculoReadDto {
  patente: string;
  nroCliente: number;
  nombreCliente?: string;
  descripcion: string;
  kilometrajeUltimoService: number;
  fechaUltimoService?: string | null;
  fechaProximoService?: string | null;
  anio?: number | null;
  nroSerieMotor?: string;
  nroSerieChasis?: string;
  codigoTecnico?: string;
  descripcionTecnico?: string;
  codigoMarca?: string;
  descripcionMarca?: string;
  codigoLinea?: string;
  descripcionLinea?: string;
  codigoModelo?: string;
  descripcionModelo?: string;
  codigoCombustible?: string;
  descripcionCombustible?: string;
}

export interface VehiculoSearchParams {
  searchTerm?: string;
  nroCliente?: number;
  pageNumber?: number;
  pageSize?: number;
}

export interface VehiculoPagedResult {
  items: VehiculoReadDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface CreateVehiculoDto {
  patente: string;
  nroCliente: number;
  descripcion?: string;
  kilometrajeUltimoService?: number | null;
  fechaUltimoService?: string | null;
  fechaProximoService?: string | null;
  anio?: number | null;
  nroSerieMotor?: string;
  nroSerieChasis?: string;
  codigoTecnico?: string;
  codigoMarca?: string;
  codigoLinea?: string;
  codigoModelo?: string;
  codigoCombustible?: string;
}

export type UpdateVehiculoDto = Omit<CreateVehiculoDto, 'patente'>;

export interface CatalogoItemDto {
  codigo: string;
  descripcion: string;
}

export interface CreateCatalogoItemDto {
  codigo: string;
  descripcion: string;
}

export interface UpdateCatalogoItemDto {
  descripcion: string;
}

export interface CreateLineaDto extends CreateCatalogoItemDto {
  codigoMarca: string;
}

export interface CreateModeloDto extends CreateCatalogoItemDto {
  codigoMarca: string;
  codigoLinea: string;
}

const str = (raw: Record<string, unknown>, camel: string, pascal: string): string =>
  String(raw[camel] ?? raw[pascal] ?? '').trim();

const num = (raw: Record<string, unknown>, camel: string, pascal: string): number => {
  const v = raw[camel] ?? raw[pascal];
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const numOrNull = (raw: Record<string, unknown>, camel: string, pascal: string): number | null => {
  const v = raw[camel] ?? raw[pascal];
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const dateOrNull = (raw: Record<string, unknown>, camel: string, pascal: string): string | null => {
  const v = raw[camel] ?? raw[pascal];
  if (v === null || v === undefined || v === '') return null;
  return String(v);
};

const normalizeVehiculo = (raw: Record<string, unknown>): VehiculoReadDto => ({
  patente: str(raw, 'patente', 'Patente'),
  nroCliente: num(raw, 'nroCliente', 'NroCliente'),
  nombreCliente: str(raw, 'nombreCliente', 'NombreCliente') || undefined,
  descripcion: str(raw, 'descripcion', 'Descripcion'),
  kilometrajeUltimoService: num(raw, 'kilometrajeUltimoService', 'KilometrajeUltimoService'),
  fechaUltimoService: dateOrNull(raw, 'fechaUltimoService', 'FechaUltimoService'),
  fechaProximoService: dateOrNull(raw, 'fechaProximoService', 'FechaProximoService'),
  anio: numOrNull(raw, 'anio', 'Anio'),
  nroSerieMotor: str(raw, 'nroSerieMotor', 'NroSerieMotor') || undefined,
  nroSerieChasis: str(raw, 'nroSerieChasis', 'NroSerieChasis') || undefined,
  codigoTecnico: str(raw, 'codigoTecnico', 'CodigoTecnico') || undefined,
  descripcionTecnico: str(raw, 'descripcionTecnico', 'DescripcionTecnico') || undefined,
  codigoMarca: str(raw, 'codigoMarca', 'CodigoMarca') || undefined,
  descripcionMarca: str(raw, 'descripcionMarca', 'DescripcionMarca') || undefined,
  codigoLinea: str(raw, 'codigoLinea', 'CodigoLinea') || undefined,
  descripcionLinea: str(raw, 'descripcionLinea', 'DescripcionLinea') || undefined,
  codigoModelo: str(raw, 'codigoModelo', 'CodigoModelo') || undefined,
  descripcionModelo: str(raw, 'descripcionModelo', 'DescripcionModelo') || undefined,
  codigoCombustible: str(raw, 'codigoCombustible', 'CodigoCombustible') || undefined,
  descripcionCombustible: str(raw, 'descripcionCombustible', 'DescripcionCombustible') || undefined,
});

const normalizeCatalogo = (raw: Record<string, unknown>): CatalogoItemDto => ({
  codigo: str(raw, 'codigo', 'Codigo'),
  descripcion: str(raw, 'descripcion', 'Descripcion'),
});

const parsePaged = (
  data: unknown,
  fallbackPage: number,
  fallbackSize: number,
): VehiculoPagedResult => {
  if (data && typeof data === 'object' && 'items' in data) {
    const paged = data as VehiculoPagedResult & {
      Items?: unknown[];
      TotalCount?: number;
      PageNumber?: number;
      PageSize?: number;
      TotalPages?: number;
    };
    const items = (paged.items ?? paged.Items ?? []) as unknown[];
    return {
      items: items.map((item) => normalizeVehiculo(item as Record<string, unknown>)),
      totalCount: paged.totalCount ?? paged.TotalCount ?? 0,
      pageNumber: paged.pageNumber ?? paged.PageNumber ?? fallbackPage,
      pageSize: paged.pageSize ?? paged.PageSize ?? fallbackSize,
      totalPages: paged.totalPages ?? paged.TotalPages ?? 0,
    };
  }
  return {
    items: [],
    totalCount: 0,
    pageNumber: fallbackPage,
    pageSize: fallbackSize,
    totalPages: 0,
  };
};

const parseCatalogoList = (data: unknown): CatalogoItemDto[] => {
  const list = Array.isArray(data)
    ? data
    : data && typeof data === 'object' && 'items' in data
      ? ((data as { items?: unknown[] }).items ?? (data as { Items?: unknown[] }).Items ?? [])
      : [];
  return (list as unknown[]).map((item) => normalizeCatalogo(item as Record<string, unknown>));
};

const vehiculoService = {
  getVehiculos: async (params: VehiculoSearchParams): Promise<VehiculoPagedResult> => {
    const pageNumber = params.pageNumber ?? 1;
    const pageSize = params.pageSize ?? 10;
    const resp = await api.get('/api/Vehiculos', {
      params: {
        SearchTerm: params.searchTerm || undefined,
        NroCliente: params.nroCliente && params.nroCliente > 0 ? params.nroCliente : undefined,
        PageNumber: pageNumber,
        PageSize: pageSize,
      },
    });
    return parsePaged(resp.data, pageNumber, pageSize);
  },

  getVehiculo: async (patente: string): Promise<VehiculoReadDto> => {
    const encoded = encodeURIComponent(patente.trim());
    const resp = await api.get(`/api/Vehiculos/${encoded}`);
    return normalizeVehiculo(resp.data as Record<string, unknown>);
  },

  createVehiculo: async (dto: CreateVehiculoDto): Promise<VehiculoReadDto> => {
    const response = await api.post('/api/Vehiculos', dto);
    return normalizeVehiculo(response.data as Record<string, unknown>);
  },

  updateVehiculo: async (patente: string, dto: UpdateVehiculoDto): Promise<void> => {
    const encoded = encodeURIComponent(patente.trim());
    await api.put(`/api/Vehiculos/${encoded}`, dto);
  },

  deleteVehiculo: async (patente: string): Promise<void> => {
    const encoded = encodeURIComponent(patente.trim());
    await api.delete(`/api/Vehiculos/${encoded}`);
  },

  getPdf: async (params: Omit<VehiculoSearchParams, 'pageNumber' | 'pageSize'>): Promise<Blob> => {
    const response = await api.get('/api/Vehiculos/pdf', {
      params: {
        SearchTerm: params.searchTerm || undefined,
      },
      responseType: 'blob',
    });
    return response.data;
  },

  getPdfPorNro: async (desde: string, hasta: string): Promise<Blob> => {
    const response = await api.get('/api/Vehiculos/pdf-por-nro', {
      params: { Desde: desde.trim(), Hasta: hasta.trim() },
      responseType: 'blob',
    });
    return response.data;
  },

  getMarcas: async (searchTerm?: string): Promise<CatalogoItemDto[]> => {
    const resp = await api.get('/api/Vehiculos/marcas', {
      params: { SearchTerm: searchTerm || undefined },
    });
    return parseCatalogoList(resp.data);
  },

  createMarca: async (dto: CreateCatalogoItemDto): Promise<CatalogoItemDto> => {
    const resp = await api.post('/api/Vehiculos/marcas', dto);
    return normalizeCatalogo(resp.data as Record<string, unknown>);
  },

  updateMarca: async (codigo: string, dto: UpdateCatalogoItemDto): Promise<void> => {
    const encoded = encodeURIComponent(codigo.trim());
    await api.put(`/api/Vehiculos/marcas/${encoded}`, dto);
  },

  deleteMarca: async (codigo: string): Promise<void> => {
    const encoded = encodeURIComponent(codigo.trim());
    await api.delete(`/api/Vehiculos/marcas/${encoded}`);
  },

  getLineas: async (marca: string, searchTerm?: string): Promise<CatalogoItemDto[]> => {
    const resp = await api.get('/api/Vehiculos/lineas', {
      params: { Marca: marca, SearchTerm: searchTerm || undefined },
    });
    return parseCatalogoList(resp.data);
  },

  createLinea: async (dto: CreateLineaDto): Promise<CatalogoItemDto> => {
    const resp = await api.post('/api/Vehiculos/lineas', dto);
    return normalizeCatalogo(resp.data as Record<string, unknown>);
  },

  updateLinea: async (
    codigo: string,
    codigoMarca: string,
    dto: UpdateCatalogoItemDto,
  ): Promise<void> => {
    const encoded = encodeURIComponent(codigo.trim());
    await api.put(`/api/Vehiculos/lineas/${encoded}`, dto, {
      params: { Marca: codigoMarca },
    });
  },

  deleteLinea: async (codigo: string, codigoMarca: string): Promise<void> => {
    const encoded = encodeURIComponent(codigo.trim());
    await api.delete(`/api/Vehiculos/lineas/${encoded}`, {
      params: { Marca: codigoMarca },
    });
  },

  getModelos: async (marca: string, linea: string, searchTerm?: string): Promise<CatalogoItemDto[]> => {
    const resp = await api.get('/api/Vehiculos/modelos', {
      params: { Marca: marca, Linea: linea, SearchTerm: searchTerm || undefined },
    });
    return parseCatalogoList(resp.data);
  },

  createModelo: async (dto: CreateModeloDto): Promise<CatalogoItemDto> => {
    const resp = await api.post('/api/Vehiculos/modelos', dto);
    return normalizeCatalogo(resp.data as Record<string, unknown>);
  },

  updateModelo: async (
    codigo: string,
    codigoMarca: string,
    codigoLinea: string,
    dto: UpdateCatalogoItemDto,
  ): Promise<void> => {
    const encoded = encodeURIComponent(codigo.trim());
    await api.put(`/api/Vehiculos/modelos/${encoded}`, dto, {
      params: { Marca: codigoMarca, Linea: codigoLinea },
    });
  },

  deleteModelo: async (codigo: string, codigoMarca: string, codigoLinea: string): Promise<void> => {
    const encoded = encodeURIComponent(codigo.trim());
    await api.delete(`/api/Vehiculos/modelos/${encoded}`, {
      params: { Marca: codigoMarca, Linea: codigoLinea },
    });
  },

  getCombustibles: async (searchTerm?: string): Promise<CatalogoItemDto[]> => {
    const resp = await api.get('/api/Vehiculos/combustibles', {
      params: { SearchTerm: searchTerm || undefined },
    });
    return parseCatalogoList(resp.data);
  },

  createCombustible: async (dto: CreateCatalogoItemDto): Promise<CatalogoItemDto> => {
    const resp = await api.post('/api/Vehiculos/combustibles', dto);
    return normalizeCatalogo(resp.data as Record<string, unknown>);
  },

  updateCombustible: async (codigo: string, dto: UpdateCatalogoItemDto): Promise<void> => {
    const encoded = encodeURIComponent(codigo.trim());
    await api.put(`/api/Vehiculos/combustibles/${encoded}`, dto);
  },

  deleteCombustible: async (codigo: string): Promise<void> => {
    const encoded = encodeURIComponent(codigo.trim());
    await api.delete(`/api/Vehiculos/combustibles/${encoded}`);
  },
};

export default vehiculoService;
